import classNames from "classnames";
import React, { Children, ReactNode, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";

type CarouselItemProps = {
  id?: string;
  className?: string;
};

const wrapCarouselItem = (
  child: ReactNode,
  name: string,
  index: number,
  className: string
) =>
  React.isValidElement<CarouselItemProps>(child)
    ? React.cloneElement<CarouselItemProps>(child, {
        className,
        id: `slide-${name}-${index}`,
      })
    : child;

export const Carousel = ({
  children,
  name,
}: {
  children: ReactNode;
  name: string;
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const updateIndex = (newIndex: number) => {
    let index;
    if (newIndex < 0) {
      index = Children.toArray(children).length - 1;
    } else if (newIndex > Children.toArray(children).length - 1) {
      index = 0;
    } else {
      index = newIndex;
    }
    const element = document.getElementById(`slide-${name}-${index}`);
    if (element) {
      element.scrollIntoView({ block: "nearest", inline: "center" });
    }
    setCurrentIndex(index);
  };
  if (!children || Children.toArray(children).length === 0) {
    return null;
  }

  return (
    <div className="relative py-5">
      <ul className="daisy-carousel w-full">
        {Children.map(children, (child, i) =>
          wrapCarouselItem(child, name, i, "daisy-carousel-item w-fit")
        )}
      </ul>
      <div className="text-3xl py-3 bottom-0 flex justify-between">
        <ChevronLeftIcon
          height={36}
          onClick={() => updateIndex(currentIndex - 1)}
          className="cursor-pointer"
        />
        <div className="text-4xl">
          {Children.map(children, (_child, i) => (
            <span
              className={classNames("cursor-pointer", {
                "text-primary-300": currentIndex === i,
              })}
              onClick={() => updateIndex(i)}
            >
              {"\u2022"}
            </span>
          ))}
        </div>
        <ChevronRightIcon
          height={36}
          onClick={() => updateIndex(currentIndex + 1)}
          className="cursor-pointer"
        />
      </div>
    </div>
  );
};

export const DoubleCarousel = ({
  children,
  name,
}: {
  children: ReactNode;
  name: string;
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const updateIndex = (newIndex: number) => {
    let index: number;
    if (newIndex < 0) {
      index = Children.toArray(children).length - 1;
    } else if (newIndex > Children.toArray(children).length - 2) {
      index = 0;
    } else {
      index = newIndex;
    }

    const getNewIndex = () => {
      if (currentIndex < index) {
        if (currentIndex + 1 === index) {
          return currentIndex + 2;
        } else {
          return index;
        }
      }
      return index;
    };
    const element = document.getElementById(`slide-${name}-${getNewIndex()}`);
    if (element) {
      element.scrollIntoView({ block: "nearest", inline: "nearest" });
    }
    setCurrentIndex(index);
  };
  if (!children || Children.toArray(children).length === 0) {
    return null;
  }
  return (
    <div className="relative py-5">
      <ul className="daisy-carousel w-full">
        {Children.map(children, (child, i) =>
          wrapCarouselItem(child, name, i, "daisy-carousel-item w-1/2")
        )}
      </ul>
      <div className="text-3xl py-3 bottom-0 flex justify-between ">
        <ChevronLeftIcon
          height={36}
          onClick={() => updateIndex(currentIndex - 1)}
          className="cursor-pointer"
        />
        <div className="text-4xl">
          {Children.map(children, (_child, i) => (
            <span
              className={classNames("", {
                "text-primary-300":
                  currentIndex === i || currentIndex + 1 === i,
              })}
            >
              {"\u2022"}
            </span>
          ))}
        </div>
        <ChevronRightIcon
          height={36}
          onClick={() => updateIndex(currentIndex + 1)}
          className="cursor-pointer"
        />
      </div>
    </div>
  );
};

export const AltCarousel = ({
  children,
  name,
  color = "#FF4532",
}: {
  children: ReactNode;
  name: string;
  color: string;
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const updateIndex = (newIndex: number) => {
    let index;
    if (newIndex < 0) {
      index = Children.toArray(children).length - 1;
    } else if (newIndex > Children.toArray(children).length - 1) {
      index = 0;
    } else {
      index = newIndex;
    }
    const element = document.getElementById(`slide-${name}-${index}`);
    if (element) {
      element.scrollIntoView({ block: "nearest", inline: "center" });
    }
    setCurrentIndex(index);
  };
  if (!children || Children.toArray(children).length === 0) {
    return null;
  }

  return (
    <div className="relative py-5 w-full ">
      <div className="w-full flex items-center">
        <div
          className=" cursor-pointer"
          style={{
            width: "0px",
            height: "0px",
            borderStyle: "solid",
            borderWidth: "0 18px 18px 18px",
            borderColor: `transparent transparent ${color} transparent`,
            transform: "rotate(-90deg)",
          }}
          onClick={() => updateIndex(currentIndex - 1)}
        ></div>
        <ul className="daisy-carousel w-full ">
          {Children.map(children, (child, i) => (
            <div
              id={`slide-${name}-${i}`}
              className="daisy-carousel-item w-full overflow-sh"
            >
              {child}
            </div>
          ))}
        </ul>
        <div
          className=" cursor-pointer"
          style={{
            width: "0px",
            height: "0px",
            borderStyle: "solid",
            borderWidth: "0 18px 18px 18px",
            borderColor: `transparent transparent ${color} transparent`,
            transform: "rotate(90deg)",
          }}
          onClick={() => updateIndex(currentIndex + 1)}
        ></div>
      </div>
    </div>
  );
};
